import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  ForgotPasswordRequest,
  MembershipPickerResponse,
  OtpLoginResponse,
  PickMembershipRequest,
  PickMembershipResponse,
  PublicInvitation,
  RequestOtpRequest,
  ResetPasswordRequest,
  ResetTokenStatus,
  VerifyOtpRequest,
} from '@velora/contracts';

/** Mirrors the server's real login-success payload (identity module). */
export interface LoginResponse {
  redirect_to: string;
  organization: { slug: string; name: string };
  institution: { slug: string; name: string; type: 'school' | 'college' };
}
export interface MagicResponse {
  magic_sent: true;
}
export type LoginInput =
  | { email: string; password: string }
  | { email: string; magic: true };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
    this.name = 'ApiError';
  }
}

// Vite bakes VITE_API_BASE_URL at build time. Blank in dev (Vite proxy handles
// /api → server); absolute in prod builds where SPA and API live on different
// origins (e.g. accounts on b-cdn.net, API on magic-containers.bunny.net).
const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const res = await fetch(`${API_BASE}/api/accounts${path}`, { ...init, headers, credentials: 'include' });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const accountsApi = {
  getInvitation(token: string): Promise<PublicInvitation> {
    return request<PublicInvitation>(`/invitations/${encodeURIComponent(token)}`);
  },
  acceptInvitation(token: string, input: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
    return request<AcceptInvitationResponse>(`/invitations/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  login(input: LoginInput): Promise<LoginResponse | MagicResponse> {
    return request<LoginResponse | MagicResponse>('/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>('/logout', { method: 'POST' });
  },
  requestOtp(input: RequestOtpRequest): Promise<{ ok: true }> {
    return request<{ ok: true }>('/login/otp/request', { method: 'POST', body: JSON.stringify(input) });
  },
  verifyOtp(input: VerifyOtpRequest): Promise<OtpLoginResponse> {
    return request<OtpLoginResponse>('/login/otp/verify', { method: 'POST', body: JSON.stringify(input) });
  },
  forgot(input: ForgotPasswordRequest): Promise<{ ok: true }> {
    return request<{ ok: true }>('/forgot', { method: 'POST', body: JSON.stringify(input) });
  },
  getResetStatus(token: string): Promise<ResetTokenStatus> {
    return request<ResetTokenStatus>(`/reset/${encodeURIComponent(token)}`);
  },
  reset(token: string, input: ResetPasswordRequest): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/reset/${encodeURIComponent(token)}`, { method: 'POST', body: JSON.stringify(input) });
  },
  getMemberships(): Promise<MembershipPickerResponse> {
    return request<MembershipPickerResponse>('/select');
  },
  pickMembership(input: PickMembershipRequest): Promise<PickMembershipResponse> {
    return request<PickMembershipResponse>('/select', { method: 'POST', body: JSON.stringify(input) });
  },
};
